// /app/frontend/src/pages/admin/AdminAnalyticsPage.jsx
/**
 * Admin Analytics Page
 * 
 * Simple counters for soft launch metrics.
 * No fancy dashboards yet - just the raw numbers that matter.
 * 
 * Current metrics:
 * - identity_started: Users who clicked "Verify My Identity"
 * - identity_verified: Users who completed verification
 */

import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/client";
import { AuthContext } from "../../App";
import { ArrowLeft, Shield, TrendingUp, Users, CheckCircle, XCircle, Activity, MessageCircle, Mic, Zap } from "lucide-react";
import { createInternalAxiosConfig } from "../../utils/internalApi";
import InfoIconButton from "../../components/ui/InfoIconButton";
import AdminHelp from "../../components/admin/AdminHelp";


export default function AdminAnalyticsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        const config = createInternalAxiosConfig(token, 'admin');
        const res = await apiClient.get(`/admin/analytics/summary`, config);
        setData(res.data);
      } catch (err) {
        console.error("[AdminAnalytics] Failed to load:", err);
        setError(err.response?.data?.detail || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-4"></div>
          <div className="text-sm text-slate-400">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">⚠️ {error}</div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition"
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  const identity = data?.identity || {};
  const users = data?.users || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="border-b border-slate-800 pb-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Admin</span>
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">
              📊 Soft Launch Analytics
            </h1>
            <AdminHelp helpKey="analytics" />
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Simple counters to track what matters. No dashboards yet.
          </p>
        </header>

        {/* Identity Verification Metrics */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Identity Verification</h2>
              <p className="text-xs text-slate-400">Is it killing conversion or filtering bad actors?</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Started */}
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-400">Started</span>
                  <InfoIconButton metricKey="identity_started" size="xs" />
                </div>
                <Activity className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-3xl font-bold text-blue-400">
                {identity.started ?? 0}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Clicked &quot;Verify My Identity&quot;
              </div>
            </div>

            {/* Verified */}
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-400">Verified</span>
                  <InfoIconButton metricKey="identity_verified" size="xs" />
                </div>
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="text-3xl font-bold text-emerald-400">
                {identity.verified ?? 0}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Completed successfully
              </div>
            </div>

            {/* Conversion Rate */}
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-400">Conversion</span>
                  <InfoIconButton metricKey="verification_conversion" size="xs" />
                </div>
                <TrendingUp className="w-4 h-4 text-purple-400" />
              </div>
              <div className="text-3xl font-bold text-purple-400">
                {identity.conversion_rate ?? 0}%
              </div>
              <div className="text-xs text-slate-500 mt-1">
                verified / started
              </div>
            </div>
          </div>

          {/* Quick insight */}
          {identity.started > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
              <div className="text-sm">
                {identity.conversion_rate >= 80 ? (
                  <span className="text-emerald-400">✅ Healthy conversion - verification isn&apos;t killing signups</span>
                ) : identity.conversion_rate >= 50 ? (
                  <span className="text-amber-400">⚠️ Moderate drop-off - monitor for friction points</span>
                ) : identity.conversion_rate > 0 ? (
                  <span className="text-red-400">🚨 High drop-off - may need UX improvements</span>
                ) : (
                  <span className="text-slate-400">📊 No completions yet - waiting for data</span>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Rooms Metrics - Trust Ladder */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-rose-500/20">
              <MessageCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Rooms: Trust Ladder</h2>
              <p className="text-xs text-slate-400">Are rooms motivating verification?</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Post Attempts (Unverified)</div>
              <div className="text-2xl font-bold text-amber-400">{data?.rooms?.post_attempts_unverified ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Wanted to post but couldn&apos;t</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Reply Attempts (Unverified)</div>
              <div className="text-2xl font-bold text-amber-400">{data?.rooms?.reply_attempts_unverified ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Wanted to reply but couldn&apos;t</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Total Posts</div>
              <div className="text-2xl font-bold text-emerald-400">{data?.rooms?.total_posts ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">By verified members</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Total Replies</div>
              <div className="text-2xl font-bold text-emerald-400">{data?.rooms?.total_replies ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">By verified members</div>
            </div>
          </div>

          {/* Smart Insight Banner */}
          {(() => {
            const totalAttempts = (data?.rooms?.post_attempts_unverified ?? 0) + (data?.rooms?.reply_attempts_unverified ?? 0);
            const ctaClicks = data?.rooms?.verify_cta_clicked ?? 0;
            const ctr = totalAttempts > 0 ? Math.round((ctaClicks / totalAttempts) * 100) : 0;
            
            if (totalAttempts === 0) {
              return (
                <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                  <div className="text-sm text-slate-400">
                    📊 No demand signals yet. Rooms aren&apos;t being tested by real users.
                  </div>
                </div>
              );
            } else if (totalAttempts <= 5) {
              return (
                <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                  <div className="text-sm text-blue-400">
                    📈 Early demand signal ({totalAttempts} attempt{totalAttempts !== 1 ? 's' : ''}). Watch trends.
                    {ctaClicks > 0 && <span className="text-slate-400"> • {ctr}% clicked verify</span>}
                  </div>
                </div>
              );
            } else if (totalAttempts <= 25) {
              return (
                <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                  <div className="text-sm text-amber-400">
                    🔥 Strong demand ({totalAttempts} attempts). Verification is being pulled by feature value.
                    {ctaClicks > 0 && <span className="text-slate-400"> • {ctr}% CTR to verify</span>}
                  </div>
                </div>
              );
            } else {
              return (
                <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                  <div className="text-sm text-emerald-400">
                    🚀 High demand ({totalAttempts} attempts). Consider nudging verification earlier.
                    {ctaClicks > 0 && <span className="text-slate-400"> • {ctr}% CTR to verify</span>}
                  </div>
                </div>
              );
            }
          })()}
        </section>

        {/* Voice Metrics */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-indigo-500/20">
              <Mic className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Voice Notes: Demand</h2>
              <p className="text-xs text-slate-400">How much do users want voice before verifying?</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Voice Attempts (Unverified)</div>
              <div className="text-2xl font-bold text-amber-400">{data?.voice?.attempts_unverified ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Tapped mic but couldn&apos;t send</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Verify CTA Clicks</div>
              <div className="text-2xl font-bold text-blue-400">{data?.voice?.verify_cta_clicked ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">{data?.voice?.ctr ?? 0}% CTR</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Verified Users</div>
              <div className="text-2xl font-bold text-emerald-400">{users.identity_verified ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Can send voice notes</div>
            </div>
          </div>
        </section>

        {/* Post-Verification Activation */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Zap className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Post-Verification Activation</h2>
              <p className="text-xs text-slate-400">Did verification lead to action, or just a badge?</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">First Room Posts</div>
              <div className="text-2xl font-bold text-emerald-400">{data?.activation?.first_room_post ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">After verifying</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">First Room Replies</div>
              <div className="text-2xl font-bold text-emerald-400">{data?.activation?.first_room_reply ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">After verifying</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">First Voice Notes</div>
              <div className="text-2xl font-bold text-emerald-400">{data?.activation?.first_voice_note ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">After verifying</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="text-sm text-slate-400 mb-1">Total Activated</div>
              <div className="text-2xl font-bold text-purple-400">{data?.activation?.total_activated ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Users who took action</div>
            </div>
          </div>

          {/* Activation Insight */}
          {users.identity_verified > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
              <div className="text-sm">
                {(() => {
                  const activated = data?.activation?.total_activated ?? 0;
                  const verified = users.identity_verified ?? 0;
                  const rate = verified > 0 ? Math.round((activated / verified) * 100) : 0;
                  
                  if (rate >= 70) {
                    return <span className="text-emerald-400">🚀 {rate}% activation rate - verification feels like an unlock, not a chore</span>;
                  } else if (rate >= 40) {
                    return <span className="text-amber-400">📈 {rate}% activation rate - decent, but some users verify and don&apos;t participate</span>;
                  } else if (rate > 0) {
                    return <span className="text-red-400">⚠️ {rate}% activation rate - many verify but don&apos;t act. Check friction.</span>;
                  } else {
                    return <span className="text-slate-400">📊 Waiting for verified users to take first actions...</span>;
                  }
                })()}
              </div>
            </div>
          )}
        </section>

        {/* User Overview */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">User Overview</h2>
              <p className="text-xs text-slate-400">Quick snapshot of the user base</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm text-slate-400">Total Users</span>
                <InfoIconButton metricKey="total_users" size="xs" />
              </div>
              <div className="text-2xl font-bold">{users.total ?? 0}</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm text-slate-400">Elite Members</span>
                <InfoIconButton metricKey="elite_members" size="xs" />
              </div>
              <div className="text-2xl font-bold text-pink-400">{users.elite ?? 0}</div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm text-slate-400">ID Verified</span>
                <InfoIconButton metricKey="identity_verified" size="xs" />
              </div>
              <div className="text-2xl font-bold text-emerald-400">{users.identity_verified ?? 0}</div>
            </div>
          </div>
        </section>

        {/* Future Placeholder */}
        <section className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-6 text-center">
          <div className="text-slate-500 text-sm">
            🔮 More metrics coming after soft launch data collection
          </div>
          <div className="text-xs text-slate-600 mt-2">
            Room vitality • Chat survival • Multi-loop engagement
          </div>
        </section>
      </div>
    </div>
  );
}
